// frontend/src/queries/client.js
//
// The single TanStack Query client + default options for the Manexys app.
// Exported as a singleton so non-React code (AuthContext logout) can reset
// the cache without going through hooks.
//
// Default config:
//
//   staleTime          0       — overridden per-resource (see options below
//                                or per-call in `useQuery`). Keeping the
//                                global default at 0 means any query that
//                                forgets to set staleTime gets safe-but-
//                                conservative behavior (always stale, every
//                                trigger refetches).
//   gcTime             30 min  — cached data sticks around 30 min after the
//                                last consumer unmounts. Survives back-nav
//                                and cross-page navigation cleanly.
//   refetchOnWindowFocus true  — when the browser window comes back,
//                                stale data refreshes in background. Per-query
//                                opt-out for truly static reference data
//                                (countries, currencies). A switch between
//                                app tabs is not a focus: a save reaches a
//                                hidden tab through queries/dataChanged.
//   refetchOnReconnect true    — same idea for network blips
//   retry              network — see `retryQueryOnce` below.
//   retryDelay         1s      — the one retry waits a second
//
// Per-resource overrides go on the individual `useQuery` calls or on the
// `defaultOptions.queries` for whole categories. See queries/keys.js for
// the key hierarchy that drives invalidation.

import { QueryClient } from '@tanstack/react-query';
import { NetworkError } from '../utils/parseApiError';

/**
 * The app's one retry rule, for reads only.
 *
 * A server that answered has said what it means — a 404, a 403 or a 500 says
 * the same thing a second later, and retrying it only delays the failure the
 * merchant needs to see (CLAUDE.md, "fallbacks cover bugs"). A request that
 * never reached the server is different: a dropped connection is not an
 * answer, and one retry a second later covers a blip without hiding anything.
 * When that retry fails too, the read ends in its error state as it would
 * have without it.
 */
export function retryQueryOnce(failureCount, error) {
  return error instanceof NetworkError && failureCount < 1;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Global defaults — per-resource overrides on each useQuery call.
      staleTime: 0,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: retryQueryOnce,
      retryDelay: 1000,
    },
    mutations: {
      // Mutations don't auto-retry — a failed POST/PUT shouldn't quietly
      // re-fire and risk double-writes. Caller decides if they want retry.
      retry: 0,
    },
  },
});


// Auth-boundary cache reset. Called from AuthContext on logout.
//
// `removeQueries({})` matches every query (no predicate). Subsequent
// reads will refetch from cold cache, which is exactly the multi-tenant
// safety property we want at auth boundaries.
export function clearAllQueries() {
  queryClient.removeQueries({});
}


// Cache reset for store-scoped data. Called when AuthContext converges on a
// different store WITHOUT a reload — a switch made in another tab, an SSO
// landing, a server-resolved default (`applyUserContext`). The switcher in
// this tab replaces the document, which discards this cache with it. Queries
// whose key includes any `storeId` segment are removed (not just invalidated)
// — we don't want any chance of serving stale store-A data under store B,
// even briefly.
// Tenant-invariant queries (countries, currencies, tabular entities) stay
// cached because they don't carry a storeId in their key.
//
// Implementation: TanStack passes each query through the predicate; we
// walk the key array and remove queries that have at least one segment
// with a `storeId` property. `removeQueries` (not `invalidateQueries`)
// because invalidate marks-stale-and-refetches but keeps the old data
// in cache; remove evicts entirely so a brief navigation back can't
// surface the previous store's rows.
/** Whether a query key carries a `{ storeId }` segment, i.e. names one store's rows. */
export function isStoreScopedKey(queryKey) {
  return queryKey.some(
    (segment) => segment !== null && typeof segment === 'object' && 'storeId' in segment,
  );
}

export function clearStoreScopedQueries() {
  queryClient.removeQueries({ predicate: (query) => isStoreScopedKey(query.queryKey) });
}
